<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.platformSkills.editGroup.title') }}</h2>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <div v-if="loadingDetail" class="modal-body state-msg">{{ $t('bo.common.loading') }}</div>

      <form v-else @submit.prevent="handleSubmit" class="modal-form">
        <div class="modal-body">
          <div class="field">
            <label>{{ $t('bo.common.name') }} <span class="required">*</span></label>
            <input v-model="form.name" type="text" required />
          </div>

          <div class="field">
            <label>{{ $t('bo.common.description') }}</label>
            <textarea v-model="form.description" rows="3" />
          </div>

          <!-- Traduction francaise (skill_groups_i18n) : affichee dans l'application en francais. -->
          <div class="translation-box">
            <div class="translation-head">
              <span class="translation-title">{{ $t('bo.platformSkills.translation.title') }}</span>
              <button type="button" class="btn-translate" :disabled="translating" @click="autoTranslate">
                {{ translating ? $t('bo.platformSkills.translation.translating') : $t('bo.platformSkills.translation.auto') }}
              </button>
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.translation.nameFr') }}</label>
              <input v-model="translationFr.name" type="text" maxlength="100" />
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.translation.descriptionFr') }}</label>
              <textarea v-model="translationFr.description" rows="2" />
            </div>
            <p class="hint">{{ translateMessage || $t('bo.platformSkills.translation.hint') }}</p>
          </div>

          <div class="field-row">
            <div class="field">
              <label>{{ $t('bo.platformSkills.groupType') }}</label>
              <select v-model="form.group_type">
                <option value="domain">{{ $t('bo.platformSkills.domain') }}</option>
                <option value="transversal">{{ $t('bo.platformSkills.transversal') }}</option>
              </select>
            </div>
            <div class="field">
              <label>{{ $t('bo.common.status') }}</label>
              <input :value="detail?.is_active === false ? $t('bo.common.archived') : $t('bo.common.active')" disabled class="input-readonly" />
            </div>
          </div>

          <!-- Skills liés -->
          <div class="section-title">{{ $t('bo.platformSkills.editGroup.linkedSkills', { count: (detail?.skills || []).length }) }}</div>
          <div class="skills-list">
            <div v-if="!(detail?.skills?.length)" class="empty-msg">{{ $t('bo.platformSkills.editGroup.noSkills') }}</div>
            <div v-else class="skills-grid">
              <span v-for="s in detail.skills" :key="s.id" class="skill-tag">{{ s.name }}</span>
            </div>
          </div>

          <!-- ESCO Mappings -->
          <div class="section-title">{{ $t('bo.platformSkills.editGroup.escoMappings') }}</div>
          <div class="esco-info">
            <p>{{ $t('bo.platformSkills.editGroup.escoMappingsDesc') }}</p>
            <div class="esco-count">
              {{ $t('bo.platformSkills.editGroup.currentlyMapped', { count: (detail?.esco_mappings || []).length }) }}
            </div>
          </div>

          <div v-if="error" class="error-msg">{{ error }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="saving">
            {{ saving ? $t('bo.common.saving') : $t('bo.common.saveChanges') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import platformSkillService from '@/services/platformSkillService'

const { t } = useI18n()

const props = defineProps({
  group: { type: Object, required: true }
})

const emit = defineEmits(['close', 'updated'])

const detail = ref(null)
const loadingDetail = ref(false)
const saving = ref(false)
const error = ref('')

const form = ref({
  name: '',
  description: '',
  group_type: 'domain',
})
// Traduction francaise du groupe (envoyee dans translations.fr a l'enregistrement).
const translationFr = ref({ name: '', description: '' })
const translating = ref(false)
const translateMessage = ref('')
const fillTranslation = (src) => {
  translationFr.value = {
    name: src?.translations?.fr?.name || '',
    description: src?.translations?.fr?.description || '',
  }
}

onMounted(async () => {
  // Start with data from list row
  form.value = {
    name: props.group.name,
    description: props.group.description || '',
    group_type: props.group.group_type || 'domain',
  }
  detail.value = props.group
  fillTranslation(props.group)

  // Fetch full detail with esco_mappings
  loadingDetail.value = true
  try {
    const res = await platformSkillService.getGroup(props.group.id)
    detail.value = res.data
    form.value = {
      name: detail.value.name,
      description: detail.value.description || '',
      group_type: detail.value.group_type || 'domain',
    }
    fillTranslation(detail.value)
  } catch (e) {
    // keep list data as fallback
  } finally {
    loadingDetail.value = false
  }
})

// Suggestion LibreTranslate a partir du nom et de la description enregistres : l'admin relit.
const autoTranslate = async () => {
  translating.value = true
  translateMessage.value = ''
  try {
    const res = await platformSkillService.autoTranslateGroup(props.group.id)
    const suggestion = res.data || {}
    if (!suggestion.available) {
      translateMessage.value = t('bo.platformSkills.translation.unavailable')
      return
    }
    translationFr.value = {
      name: suggestion.name || translationFr.value.name,
      description: suggestion.description || translationFr.value.description,
    }
    translateMessage.value = t('bo.platformSkills.translation.review')
  } catch {
    translateMessage.value = t('bo.platformSkills.translation.unavailable')
  } finally {
    translating.value = false
  }
}

const handleSubmit = async () => {
  saving.value = true
  error.value = ''
  try {
    await platformSkillService.updateGroup(props.group.id, {
      ...form.value,
      translations: {
        fr: { name: translationFr.value.name.trim(), description: translationFr.value.description.trim() },
      },
    })
    emit('updated')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.common.failedSave')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.125rem;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
}

.btn-close:hover { color: #374151; }

.modal-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.state-msg {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
}

.field {
  margin-bottom: 1.25rem;
  flex: 1;
}

.field-row {
  display: flex;
  gap: 1rem;
}

.field label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}

.required { color: #ef4444; }

.field input,
.field select,
.field textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  box-sizing: border-box;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.input-readonly { background: #f9fafb; color: #6b7280; }

.field textarea { resize: vertical; }

.section-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #374151;
  margin-bottom: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

.esco-info {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
}

.skills-list {
  margin-bottom: 1.25rem;
}

.skills-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  max-height: 160px;
  overflow-y: auto;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background: #f9fafb;
}

.skill-tag {
  background: #e0f2fe;
  color: #0369a1;
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
}

.empty-msg {
  font-size: 0.875rem;
  color: #9ca3af;
  font-style: italic;
}

.esco-info p {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  color: #64748b;
}

.esco-count {
  font-size: 0.875rem;
  color: #374151;
}

.error-msg {
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-primary {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.translation-box { border: 1px solid #e5e7eb; border-radius: 0.75rem; padding: 0.9rem 1rem; background: #fafafa; display: flex; flex-direction: column; gap: 0.6rem; }
.translation-head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; }
.translation-title { font-weight: 600; color: #1f2937; font-size: 0.9rem; }
.btn-translate { border: 1px solid #3b82f6; color: #3b82f6; background: #fff; border-radius: 0.5rem; padding: 0.35rem 0.8rem; font-size: 0.8rem; cursor: pointer; }
.btn-translate:disabled { opacity: 0.6; cursor: default; }
.translation-box .hint { margin: 0; color: #6b7280; font-size: 0.8rem; }
</style>
